<template>
  <!--
    v-bind="props" spreads all props. The three explicit bindings below override
    minDate, maxDate, and presetDates with normalized local Date objects so the
    picker never receives raw YYYY-MM-DD strings, which JavaScript parses as UTC
    midnight and shifts to the wrong local day in timezones west of UTC.
  -->
  <VueDatePicker
    :ui="{ menu: menuClassName }"
    ref="datepicker"
    v-bind="props"
    :min-date="normalizedMinDate ?? undefined"
    :max-date="normalizedMaxDate ?? undefined"
    :preset-dates="normalizedPresets"
    v-model="internalModel"
    :multi-calendars="{ solo: false, static: true }"
    @internal-model-change="onRangeSelect"
    position="right"
    :format="format"
    :locale="locale"
    :model-type="modelType"
    @range-start="onRangeStart"
  >
    <template #clear-icon />
    <template #action-preview />
    <template #dp-input="{ value }">
      <v-text-field
        :class="{ 'value-selected': internalModel }"
        :model-value="value"
        density="compact"
        rounded
        :readonly="true"
        :placeholder="placeholder"
        :aria-label="placeholder"
        :disabled="disabled"
        append-inner-icon="$triangle"
        bg-color="white"
        hide-details
        base-color="primary"
        role="button"
      ></v-text-field>
    </template>
    <template #action-buttons>
      <v-btn rounded variant="text" class="mr-2" @click="resetDate">
        {{ $t("dateRangePicker.reset") }}
      </v-btn>
      <v-btn rounded variant="text" class="mr-2" @click="closeMenu">
        {{ $t("dateRangePicker.cancel") }}
      </v-btn>
      <v-btn
        color="primary"
        rounded
        variant="flat"
        :disabled="isSelectDateDisabled"
        @click="selectDate"
      >
        {{ $t("dateRangePicker.select") }}
      </v-btn>
    </template>
  </VueDatePicker>
</template>

<script setup lang="ts">
import { computed, ref } from "vue";
import VueDatePicker from "@vuepic/vue-datepicker";
import type { VueDatePickerProps, DatePickerInstance } from "@vuepic/vue-datepicker";
import "@vuepic/vue-datepicker/dist/main.css";
import moment from "moment";
import { getDateRangePresets } from "@/constants/date-range-picker";
import type { TimeFrame } from "@/types";
import { getFormattedDateTime } from "@/_helpers/date";
import { i18n } from "@/plugins/i18n.ts";

const DATE_ONLY_FORMAT = "YYYY-MM-DD";

// Native Date object, a YYYY-MM-DD calendar date string, or an ISO 8601 date-time string.
type InternalDateValue = Date | string;

// Parses a stored date string into a local Date. YYYY-MM-DD is parsed strictly as a local
// calendar date to avoid the UTC midnight trap of new Date("YYYY-MM-DD"), which shifts the
// date by one day in timezones west of UTC (e.g. US timezones). ISO 8601 date-time strings
// (the format emitted when the time picker is enabled) carry their own offset, so they are
// safe to parse as-is. Anything else is rejected instead of falling back to new Date().
const parseDate = (value: InternalDateValue | undefined | null): Date | null => {
  if (!value) return null;
  if (value instanceof Date) return value;
  const parsed = moment(value, [DATE_ONLY_FORMAT, moment.ISO_8601], true);
  return parsed.isValid() ? parsed.toDate() : null;
};

export type DateRangePickerProps = Pick<
  VueDatePickerProps,
  | "minDate"
  | "maxDate"
  | "presetDates"
  | "disabled"
  | "teleport"
  | "enableTimePicker"
  | "config"
  | "startTime"
  | "range"
  | "modelType"
> & {
  modelValue: TimeFrame;
  placeholder: string;
  hideMenuArrow?: boolean;
};

const props = withDefaults(defineProps<DateRangePickerProps>(), {
  presetDates: () => [...getDateRangePresets()],
  minDate: undefined,
  maxDate: undefined,
  enableTimePicker: false,
  config: () => ({ shadowDom: true }),
  startTime: () => [
    { hours: 0, minutes: 0 },
    { hours: 23, minutes: 59 },
  ],
  range: () => ({
    showLastInRange: false,
  }),
});
const emit = defineEmits<{
  (e: "update:modelValue", value: TimeFrame): void;
  (e: "update:rangeStart", value: string): void;
}>();

// Serializes a Date for the app state (store, API adapters, filter objects). With the time
// picker enabled the selected hours and minutes are part of the value, so the full instant is
// kept as an ISO 8601 string. Otherwise only the local calendar date is kept as YYYY-MM-DD,
// which is what the rest of the app expects.
const formatDate = (value: InternalDateValue | undefined | null): string => {
  if (!value) return "";
  if (typeof value === "string") return value;
  return props.enableTimePicker ? value.toISOString() : moment(value).format(DATE_ONLY_FORMAT);
};

// Normalization boundary: the app state holds date strings throughout (store, API
// adapters, filter objects). VueDatePicker works internally with local Date objects.
// This computed converts at the boundary in both directions so neither side needs
// to change.
const internalModel = computed({
  get() {
    const start = parseDate(props.modelValue.start);
    const end = parseDate(props.modelValue.end);
    return start && end ? [start, end] : null;
  },
  set(value: [InternalDateValue, InternalDateValue] | null) {
    const [start, end] = value ?? [null, null];
    emit("update:modelValue", {
      start: formatDate(start),
      end: formatDate(end),
    });
  },
});
const datepicker = ref<DatePickerInstance>(null);
const selectedRange = ref<[InternalDateValue, InternalDateValue] | null>(null);

// These three computeds ensure VueDatePicker always receives local Date objects
// instead of raw YYYY-MM-DD strings. The explicit bindings below override the
// same props coming from v-bind="props" — in Vue 3, individual bindings placed
// after v-bind win when there is a conflict.
const normalizedMinDate = computed(() => parseDate(props.minDate));

const normalizedMaxDate = computed(() => parseDate(props.maxDate));

const normalizedPresets = computed(() =>
  props.presetDates?.map((preset) => {
    // Fall back to the original value if parseDate returns null (e.g. for
    // unsupported string formats or refs), so the preset is never silently dropped.
    const value = Array.isArray(preset.value)
      ? preset.value.map((v) => parseDate(v) ?? (v as Date))
      : (parseDate(preset.value as InternalDateValue) ?? (preset.value as Date));
    return { ...preset, value };
  })
);
const menuClassName = computed(() => {
  return props.hideMenuArrow ? "coro-datepicker coro-datepicker--no-arrow" : "coro-datepicker";
});
const isSelectDateDisabled = computed(() => {
  return selectedRange.value?.length !== 2;
});

const locale = computed(() => {
  return i18n.global.locale.value;
});

const onRangeSelect = (range: [InternalDateValue, InternalDateValue]) => {
  selectedRange.value = range;
};

const onRangeStart = (rangeStart: InternalDateValue) => {
  emit("update:rangeStart", formatDate(rangeStart));
};

const selectDate = () => {
  if (!selectedRange.value) {
    datepicker.value?.clearValue();
    return;
  }
  datepicker.value?.selectDate();
};

const closeMenu = () => {
  datepicker.value?.closeMenu();
};

const resetDate = () => {
  datepicker.value?.updateInternalModelValue(null);
  emit("update:rangeStart", "");
};

const format = ([startDate, endDate]: Date[]) => {
  return `${getFormattedDateTime(startDate, "L")} - ${getFormattedDateTime(endDate, "L")}`;
};
</script>

<style lang="scss">
.coro-datepicker {
  --dp-font-family: "Source Sans Pro", sans-serif !important;
  --dp-text-color: rgb(var(--v-theme-primary));
  --dp-primary-color: rgb(var(--v-theme-primary));
  --dp-hover-text-color: rgb(var(--v-theme-primary));
  --dp-hover-color: rgb(var(--v-theme-indigo-pale));
  --dp-range-between-dates-background-color: rgb(var(--v-theme-indigo-faint));
  --dp-border-radius: 8px;
  &--no-arrow {
    .dp__arrow_bottom {
      display: none !important;
    }
  }
  .dp__arrow_top {
    display: none !important;
  }
  .dp__menu {
    box-shadow: 0 4px 16px 0 rgb(var(--v-theme-gray-pale)) !important;
  }
  .dp__input_wrap {
    box-sizing: border-box;
  }
  .dp__btn.dp--preset-range {
    color: rgb(var(--v-theme-primary));
    border-radius: 4px !important;
    padding: 6px;
  }

  .v-field__append-inner > .v-icon {
    opacity: var(--v-medium-emphasis-opacity) !important;
  }
}
</style>

<style scoped lang="scss">
:deep(*) {
  --dp-font-family: "Source Sans Pro", sans-serif !important;
  --dp-text-color: rgb(var(--v-theme-primary));
  --dp-primary-color: rgb(var(--v-theme-primary));
  --dp-hover-text-color: rgb(var(--v-theme-primary));
  --dp-hover-color: rgb(var(--v-theme-indigo-pale));
  --dp-range-between-dates-background-color: rgb(var(--v-theme-indigo-faint));
  --dp-border-radius: 8px;
  .coro-datepicker--no-arrow {
    .dp__arrow_bottom {
      display: none !important;
    }
  }
  .dp__arrow_top {
    display: none !important;
  }
  .dp__menu {
    box-shadow: 0 4px 16px 0 rgb(var(--v-theme-gray-pale)) !important;
  }
  .dp__input_wrap {
    box-sizing: border-box;
  }
  .dp__btn.dp--preset-range {
    color: rgb(var(--v-theme-primary));
    border-radius: 4px !important;
    padding: 6px;
  }

  .v-field__append-inner > .v-icon {
    opacity: var(--v-medium-emphasis-opacity) !important;
  }

  .v-btn.bg-primary.v-btn--variant-flat.v-btn--disabled {
    padding: 0 18px;
  }
}
:deep(.v-input__control) {
  .icon.icon-arrow-down-xs {
    color: black;
    opacity: 1;
    font-size: 24px;
  }
  input::placeholder {
    color: rgb(var(--v-theme-primary)) !important;
    opacity: 0.9 !important;
    font-family: "Source Sans Pro", sans-serif !important;
  }
}

:deep(.value-selected) {
  .v-field__outline {
    --v-field-border-opacity: 1 !important;
    .v-field__outline__start {
      border-color: rgb(var(--v-theme-primary)) !important;
      border-top-width: 2px;
      border-left-width: 2px;
      border-bottom-width: 2px;
    }
    .v-field__outline__end {
      border-color: rgb(var(--v-theme-primary)) !important;
      border-top-width: 2px;
      border-right-width: 2px;
      border-bottom-width: 2px;
    }
  }
}
</style>
